import { computed, inject, Injectable, Provider, Signal } from '@angular/core';
import { NavRegistry } from './nav-registry';
import { NAV_SOURCE, NavItem, NavSource } from './nav-source';

/**
 * The default {@link NavSource}: projects the {@link NavRegistry}'s
 * top-level `sidebar`-surface descriptors into {@link NavItem}s.
 *
 * This is navigation source #1 — the registry-derived defaults that
 * personalised sources (pinned / recent, future) compose on top of.
 *
 * ### `canAccess()` is a sync best-effort `true`
 *
 * The real (async) visibility gate stays the per-item `*authGuard`
 * structural directive in the rendering surface — it evaluates the
 * owning `descriptor.security` through the same
 * `SecurityConstraintsEvaluator` the route guard uses. `NavItem` is a
 * synchronous contract and cannot await; rather than introduce a new
 * async-resolution path into the sidebar, `canAccess()` returns `true`
 * here and the surface defers the decision to `*authGuard`. Items the
 * user cannot access are simply never inserted into the DOM by the
 * directive. (Phase 1's `NavItem.canAccess` doc-comment notwithstanding,
 * we deliberately do NOT use it as the registry-item gate.)
 */
@Injectable({ providedIn: 'root' })
export class RegistryNavSource implements NavSource {
  private readonly _registry = inject(NavRegistry);

  /**
   * Live projection of the registry's top-level `sidebar`-surface
   * descriptors into {@link NavItem}s. Recomputes when the registry's
   * `tree` signal changes (descriptors are eager today, but the
   * computed keeps the source reactive for future dynamic sources).
   */
  public items(): Signal<NavItem[]> {
    return computed<NavItem[]>(() =>
      this._registry
        .itemsForSurface('sidebar')
        .map((d) => ({
          id: d.id,
          label: d.label,
          icon: d.icon,
          target: d.path,
          badge: d.badge,
          // Propagate the descriptor's ordering hint so the surface can
          // render groups + items in the intended sequence rather than
          // collapsing to alphabetical.
          order: d.order,
          // Project the descriptor's sidebar-surface children into the
          // sub-items the surface renders as the group's sub-navigation.
          // `childrenForSurface` already applies the surface filter +
          // the registry's stable ordering (group → order → label).
          subItems: this._registry
            .childrenForSurface(d.id, 'sidebar')
            .map((c) => ({ id: c.id, label: c.label, target: c.path })),
          source: 'registry' as const,
          // Sync best-effort — real async gate is `*authGuard` in the
          // surface, keyed on this descriptor's `security`.
          canAccess: () => true,
        })),
    );
  }
}

/**
 * Provides {@link RegistryNavSource} as navigation source #1 against the
 * {@link NAV_SOURCE} multi-token. Call once in the application providers
 * (or rely on `providedIn: 'root'` + an explicit `NAV_SOURCE` binding).
 */
export function provideRegistryNavSource(): Provider {
  return {
    provide: NAV_SOURCE,
    useExisting: RegistryNavSource,
    multi: true,
  };
}
